import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, ExternalLink } from 'lucide-react';
import { tdlExpiryApi, tdlRequirementsApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import TdlRequirementsPanel from '../components/Tdl/TdlRequirementsPanel';
import {
  ExpiryRow, CYCLE_LABELS, fmt, toYMD, daysBetween,
  isExpiredDate, hasActiveTexpiry, money,
} from '../utils/tdlExpiry';

/**
 * One labelled cell in the details column. Short values sit two to a row;
 * pass `wide` for the long ones (names, token) so they take the full width.
 */
const Field: React.FC<{ label: string; children: React.ReactNode; wide?: boolean }> = ({ label, children, wide }) => (
  <div className={wide ? 'col-span-2 sm:col-span-3 lg:col-span-2' : ''}>
    <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">{label}</div>
    <div className="text-[15px] font-medium text-gray-900">{children}</div>
  </div>
);

const TdlProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [project, setProject] = useState<ExpiryRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await tdlRequirementsApi.getProjectDetail(Number(id));
      if (res.success && res.data?.project) {
        setProject(res.data.project);
      } else {
        setNotFound(true);
      }
    } catch (e: any) {
      showError('Error', e.message || 'Failed to load the TDL project');
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id, showError]);

  useEffect(() => { load(); }, [load]);

  const copyToken = async () => {
    if (!project) return;
    try {
      await navigator.clipboard.writeText(tdlExpiryApi.publicCheckUrl(project.token));
      setCopied(true);
      showSuccess('Copied', 'API URL copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      showError('Copy failed', 'Could not write to the clipboard');
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-400">Loading TDL project…</div>;
  }

  if (notFound || !project) {
    return (
      <div className="p-6">
        <button onClick={() => navigate('/tdl/expiry')} className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline mb-3">
          <ArrowLeft size={15} /> Back to TDL Expiry
        </button>
        <p className="text-sm text-gray-500">This TDL project no longer exists.</p>
      </div>
    );
  }

  const activeTexpiry = hasActiveTexpiry(project);
  const expiredOriginal = isExpiredDate(toYMD(project.expiry_date));
  const effectiveExpired = activeTexpiry ? isExpiredDate(toYMD(project.texpiry)) : expiredOriginal;

  return (
    // Full width: details in a narrow left column, requirements fill the rest.
    <div className="min-h-screen bg-gray-100 p-3 flex flex-col lg:flex-row gap-3 items-start">
      {/* ── 1. TDL details — stacked top-to-bottom, ~20% of the screen ── */}
      <div className="w-full lg:w-1/5 lg:shrink-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-x-4 gap-y-5">
          <Field label="Customer" wide>{project.customer_name}</Field>
          <Field label="TDL Name" wide>{project.tdl_name}</Field>
          <Field label="Release Version">
            <span className="font-mono text-sm text-gray-800">{project.release_version || '—'}</span>
          </Field>
          <Field label="Billing Cycle">{CYCLE_LABELS[project.billing_cycle] || project.billing_cycle}</Field>

          <Field label="Token Status">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
              project.is_active
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-red-50 text-red-600 border-red-200'
            }`}>
              {project.is_active ? 'Active' : 'Inactive'}
            </span>
          </Field>
          <Field label="Total Amount">
            <span className="font-mono tabular-nums">{money(project.total_amount)}</span>
          </Field>
          <Field label="AMC Amount">
            <span className="font-mono tabular-nums">{money(project.amc_amount)}</span>
          </Field>
          <Field label="1st Activation">
            <span className="tabular-nums">{fmt(project.first_activation_date)}</span>
          </Field>
          <Field label="Cycle Start">
            <span className="tabular-nums">{fmt(project.start_date)}</span>
          </Field>

          <Field label="Expiry Date">
            {!project.expiry_date
              ? <span className="text-gray-400">—</span>
              : <span className={`font-semibold tabular-nums ${
                  activeTexpiry ? 'text-gray-400 line-through' : expiredOriginal ? 'text-red-600' : 'text-gray-700'
                }`}>{fmt(project.expiry_date)}</span>}
          </Field>
          <Field label="T-Expiry (Extension)">
            {activeTexpiry
              ? <span className={`font-semibold tabular-nums ${
                  isExpiredDate(toYMD(project.texpiry)) ? 'text-red-600' : 'text-amber-600'
                }`}>
                  {fmt(project.texpiry)}
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    +{daysBetween(toYMD(project.expiry_date), toYMD(project.texpiry))}d
                  </span>
                </span>
              : <span className="text-gray-400">—</span>}
          </Field>
          <Field label="Effective Expiry">
            {!project.expiry_date
              ? <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">Pending</span>
              : <span className={`font-semibold px-2.5 py-1 rounded-full text-sm tabular-nums ${
                  effectiveExpired ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'
                }`}>
                  {activeTexpiry ? fmt(project.texpiry) : fmt(project.expiry_date)}
                </span>}
          </Field>
          <Field label="Token / API URL" wide>
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs text-gray-600 truncate max-w-[130px]" title={project.token}>
                {project.token.slice(0, 12)}…
              </span>
              <button onClick={copyToken} title="Copy API URL"
                className="text-gray-400 hover:text-blue-600 transition-colors p-0.5 shrink-0">
                {copied ? <Check size={15} className="text-green-500" /> : <Copy size={15} />}
              </button>
              <a href={tdlExpiryApi.publicCheckUrl(project.token)} target="_blank" rel="noreferrer"
                title="Open API URL" className="text-gray-400 hover:text-blue-600 transition-colors p-0.5 shrink-0">
                <ExternalLink size={15} />
              </a>
            </div>
          </Field>

          {project.remark && (
            <Field label="Remark" wide>
              <span className="text-gray-600 whitespace-pre-wrap">{project.remark}</span>
            </Field>
          )}
        </div>
      </div>

      {/* ── 2. Requirements — takes the remaining width ── */}
      <div className="w-full lg:flex-1 lg:min-w-0">
        <TdlRequirementsPanel project={project} />
      </div>
    </div>
  );
};

export default TdlProjectDetail;
